// Overview screen's live-signal feed (WP1 Task 6). Fetches the signal-source
// endpoints in parallel, tolerating per-signal failure honestly: any single
// GET failing maps ONLY that signal to null/'unknown' (path-engine renders
// honest absence, spec §7) instead of the whole screen. `kind: 'error'` is
// reserved for the case where EVERY signal is unreachable — there is nothing
// honest left to show.
//
// `tier` comes from useMe() rather than a fetch of its own — the shell
// (PanelShell) already blocks rendering of every child route, this screen
// included, until useMe() resolves (panel-shell.tsx: `if (meState.kind !==
// 'ready' ...) return <div .../>`), so by the time this hook's effect runs
// useMe()'s module-level cache is already warm. The endpoints below don't
// share `ensureDefaultOrg` races the way /api/me + /api/sites do, so they
// fire together rather than being sequenced behind useMe().
//
// `gbpState`/`unrepliedReviews` come from useGbpData() (WP1 Task 8) the same
// way `tier` comes from useMe() — a separate module-cached hook, merged into
// the final returned input rather than folded into the Promise.all below.
// This is the shared, single-flight canonical GBP GET this hook used to
// call directly (see the pre-Task-8 SEAM comment this replaced); it's also
// why a GBP fetch failure alone never flips this hook's overall `kind` to
// 'error' any more — that failure now degrades independently to
// `gbpState: 'unknown'`, exactly like every other per-signal failure below,
// rather than folding into the "did every endpoint fail" check (which now
// covers only the 6 signals actually fetched in the Promise.all).
import { useEffect, useState } from 'react';
import { locationApi } from '@/lib/api-paths';
import { api } from '@/lib/api';
import { useMe } from '@/hooks/use-me';
import { useGbpData, type GbpDataState } from '@/hooks/use-gbp-data';
import type { PathEngineInput } from '@/lib/path-engine';

export type OverviewSignalsState =
  | { kind: 'loading' }
  | { kind: 'ready'; input: PathEngineInput }
  | { kind: 'error'; message: string };

interface FetchResult<T> {
  ok: boolean;
  value: T;
}

interface SitesGetResponse {
  site: { status: PathEngineInput['siteStatus'] };
}

async function fetchSiteStatus(businessId: string, locationId: string): Promise<FetchResult<PathEngineInput['siteStatus']>> {
  try {
    const res = await api.get<SitesGetResponse>(locationApi(businessId, locationId, ''));
    return { ok: true, value: res.site.status };
  } catch {
    // Conservative default: never claim "published" on an unknown fetch —
    // the setup-step "done" check only turns true on a confirmed 'published'.
    return { ok: false, value: 'draft' };
  }
}

interface GbpSignals {
  gbpState: PathEngineInput['gbpState'];
  unrepliedReviews: PathEngineInput['unrepliedReviews'];
}

// Pure mapping from useGbpData()'s state to this hook's signal shape — the
// exact parsing the pre-Task-8 direct `fetchGbp` used to do inline, now
// applied to the shared hook's already-resolved data instead of a fresh
// fetch. `loading` degrades the same as `error` here (both "we don't know
// yet/couldn't find out") — the caller only reads this once its own 6-way
// Promise.all has resolved, by which point useGbpData has virtually always
// settled too (its own fetch races the same page load), but this keeps the
// mapping total and honest for the rare case it hasn't.
function gbpDataToSignals(gbp: GbpDataState): GbpSignals {
  if (gbp.kind !== 'ready') return { gbpState: 'unknown', unrepliedReviews: null };
  const data = gbp.data;
  if (data.state !== 'connected') return { gbpState: data.state, unrepliedReviews: null };
  // Optional-chained on `data.reviews` itself too, even though the real
  // wire type always sends that key on a genuinely 'connected' response —
  // defensive against deploy-skew (an older/newer server) the same way the
  // pre-Task-8 direct fetch's local wire type modeled it as optional.
  const reviews = data.reviews?.data?.reviews;
  const unrepliedReviews = Array.isArray(reviews)
    ? reviews.filter((r) => !r.ownerReply && r.reply?.status !== 'posted').length
    : null;
  return { gbpState: 'connected', unrepliedReviews };
}

interface TrackingSignals {
  trackingEnabled: PathEngineInput['trackingEnabled'];
  trackingLastRunAt: PathEngineInput['trackingLastRunAt'];
}

async function fetchTrackingSchedule(businessId: string, locationId: string): Promise<FetchResult<TrackingSignals>> {
  try {
    const res = await api.get<{ schedule: { enabled: boolean; lastRunAt: string | null } | null }>(
      locationApi(businessId, locationId, 'geo/schedule'),
    );
    return {
      ok: true,
      value: {
        trackingEnabled: res.schedule?.enabled ?? false,
        trackingLastRunAt: res.schedule?.lastRunAt ?? null,
      },
    };
  } catch {
    return { ok: false, value: { trackingEnabled: false, trackingLastRunAt: null } };
  }
}

async function fetchHasCompletedScan(businessId: string, locationId: string): Promise<FetchResult<boolean>> {
  try {
    const res = await api.get<{ searches: { checks: { status: string }[] }[] }>(
      locationApi(businessId, locationId, 'geo/scans'),
    );
    const hasCompletedScan = res.searches.some((search) =>
      search.checks.some((check) => check.status === 'complete'),
    );
    return { ok: true, value: hasCompletedScan };
  } catch {
    return { ok: false, value: false };
  }
}

async function fetchOpenTasks(businessId: string, locationId: string): Promise<FetchResult<number | null>> {
  try {
    const res = await api.get<{ recommendations: unknown[] }>(
      locationApi(businessId, locationId, 'get-found'),
    );
    return { ok: true, value: res.recommendations.length };
  } catch {
    return { ok: false, value: null };
  }
}

async function fetchUnreadInbox(businessId: string, locationId: string): Promise<FetchResult<number | null>> {
  try {
    const res = await api.get<{ count: number }>(
      locationApi(businessId, locationId, 'forms/unread-count'),
    );
    return { ok: true, value: res.count };
  } catch {
    return { ok: false, value: null };
  }
}

async function fetchAiTrackingEnabled(businessId: string, locationId: string): Promise<FetchResult<boolean>> {
  try {
    const res = await api.get<{ tracking: { enabled: boolean } | null }>(
      locationApi(businessId, locationId, 'ai-visibility/tracking'),
    );
    return { ok: true, value: res.tracking?.enabled ?? false };
  } catch {
    return { ok: false, value: false };
  }
}

// The non-tier, non-gbp signals, fetched together. Keeping tier (useMe()) and
// gbpState/unrepliedReviews (useGbpData(), WP1 Task 8) out of this state lets
// the fetch effect re-key only on the tenant pair, and lets both of those flow
// through independently on every render (module caches, no extra fetch).
type SignalsFetchState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; input: Omit<PathEngineInput, 'tier' | 'gbpState' | 'unrepliedReviews'> };

export function useOverviewSignals(businessId: string, locationId: string): OverviewSignalsState {
  const meState = useMe();
  const gbp = useGbpData(businessId, locationId);
  const [signals, setSignals] = useState<SignalsFetchState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setSignals({ kind: 'loading' });

    // Both ids are required for a Location read. Missing either is not an
    // empty result to render — it is "no Location in scope", so nothing is
    // requested and the hook stays in `loading` until the route supplies one.
    if (!businessId || !locationId) return;

    Promise.all([
      fetchSiteStatus(businessId, locationId),
      fetchTrackingSchedule(businessId, locationId),
      fetchHasCompletedScan(businessId, locationId),
      fetchOpenTasks(businessId, locationId),
      fetchUnreadInbox(businessId, locationId),
      fetchAiTrackingEnabled(businessId, locationId),
    ]).then(([site, tracking, hasCompletedScan, openTasks, unreadInbox, aiTrackingEnabled]) => {
      if (cancelled) return;

      const results = [site, tracking, hasCompletedScan, openTasks, unreadInbox, aiTrackingEnabled];
      if (results.every((r) => !r.ok)) {
        setSignals({
          kind: 'error',
          message: "We couldn't load this business's overview right now. Try refreshing.",
        });
        return;
      }

      setSignals({
        kind: 'ready',
        input: {
          siteStatus: site.value,
          trackingEnabled: tracking.value.trackingEnabled,
          trackingLastRunAt: tracking.value.trackingLastRunAt,
          aiTrackingEnabled: aiTrackingEnabled.value,
          hasCompletedScan: hasCompletedScan.value,
          openTasks: openTasks.value,
          unreadInbox: unreadInbox.value,
          now: new Date(),
        },
      });
    });

    return () => {
      cancelled = true;
    };
  }, [businessId, locationId]);

  if (meState.kind === 'error') return { kind: 'error', message: meState.message };
  if (meState.kind !== 'ready') return { kind: 'loading' };
  if (signals.kind !== 'ready') return signals;

  return {
    kind: 'ready',
    input: { ...signals.input, ...gbpDataToSignals(gbp), tier: meState.me.usage.tier },
  };
}
